import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PAGINATION } from "@unreserved/constants";
import { useToast } from "@unreserved/hooks";
import {
  type AddEnquiryCommentResponseDto,
  type BookInspectionEnquiryApiResponseDto,
  type DocumentRequestEnquiryApiResponseDto,
  type EnquiryControllerGetMyEnquiriesV1StatusEnum,
  type EnquiryControllerGetMyEnquiriesV1TypeEnum,
  type GeneralEnquiryApiResponseDto,
  type GeneralEnquiryInputDtoQueryEnum,
  type GetEnquiryCommentsResponseDto,
  type GetEnquiryDetailsApiResponseDto,
  type GetMyEnquiriesResponseDto,
  type GetPropertiesResponseDto,
  type SendEnquiryDocumentsResponseDto,
  UpdateEnquiryStatusBodyDtoStatusEnum,
  type UpdateEnquiryStatusResponseDto,
  type UpdateInspectionApprovalResponseDto,
} from "@unreserved/sdk";
import { enquiryApi, inspectionApi } from "@unreserved/services/sdk-client";
import { handleHttpError } from "@unreserved/utils/http-error";

export const MY_ENQUIRY_QUERY_KEY = ["my-enquiry"] as const;
export const ENQUIRY_DETAILS_QUERY_KEY = ["enquiry-details"] as const;
export const ENQUIRY_COMMENTS_QUERY_KEY = ["enquiry-comments"] as const;
export const PROPERTIES_FOR_ENQUIRY_QUERY_KEY = ["properties-for-enquiry"] as const;
export const CREATE_DOCUMENT_REQUEST_MUTATION_KEY = ["create-document-request"] as const;
export const CREATE_GENERAL_ENQUIRY_MUTATION_KEY = ["create-general-enquiry"] as const;
export const CREATE_INSPECTION_MUTATION_KEY = ["create-inspection"] as const;
export const CREATE_PROPERTY_VALUATION_ENQUIRY_MUTATION_KEY = [
  "create-property-valuation-enquiry",
] as const;
export const ADD_ENQUIRY_COMMENT_MUTATION_KEY = ["add-enquiry-comment"] as const;

/** API response for property valuation enquiry creation */
export interface PropertyValuationEnquiryApiResponseDto {
  statusCode?: number;
  status: string;
  message: string;
  data: { enquiryId: string };
  error: string | null;
}

export interface UseMyEnquiryParams {
  type: EnquiryControllerGetMyEnquiriesV1TypeEnum;
  status?: EnquiryControllerGetMyEnquiriesV1StatusEnum[];
  pageSize?: number;
}

export const useMyEnquiry = (params: UseMyEnquiryParams) => {
  const { type, status, pageSize = PAGINATION.DEFAULT_LIMIT } = params;
  const statusArray = status?.length ? status : undefined;

  return useInfiniteQuery<GetMyEnquiriesResponseDto>({
    queryKey: [...MY_ENQUIRY_QUERY_KEY, type, statusArray ?? [], pageSize],
    queryFn: async ({ pageParam }) => {
      const response = await enquiryApi.enquiryControllerGetMyEnquiriesV1({
        type,
        pageNo: pageParam as number,
        pageSize,
        ...(statusArray?.length ? { status: statusArray } : {}),
      });
      return response;
    },

    getNextPageParam: (lastPage) => {
      const pagination = lastPage.data?.pagination;
      if (!pagination) return undefined;
      const { pageNo, totalPages } = pagination;
      return pageNo < totalPages ? pageNo + 1 : undefined;
    },
    initialPageParam: 1,
    staleTime: 1000 * 60 * 5,
  });
};

export interface UsePropertiesForEnquiryParams {
  /** Optional: filter by property ID */
  propertyId?: string;
  /** Optional: filter by property title (search) */
  propertyTitle?: string;
  /** Optional: filter by private inspection allowed; pass "yes" to filter, null/undefined to omit */
  privateInspectionAllowed?: "yes" | null;
  /** Optional: filter by has open inspections; pass "yes" to filter, null/undefined to omit */
  hasOpenInspections?: "yes" | null;
  pageSize?: number;
}

/**
 * Fetches paginated list of properties that have open inspection slots and documents.
 * Each property includes id, title, slug, listingId, openInspections, and documents.
 * Optional filters: propertyId, propertyTitle, privateInspectionAllowed, hasOpenInspections.
 */
export const usePropertiesForEnquiry = (params: UsePropertiesForEnquiryParams = {}) => {
  const {
    propertyId,
    propertyTitle,
    privateInspectionAllowed,
    hasOpenInspections,
    pageSize = PAGINATION.DEFAULT_LIMIT,
  } = params;

  return useInfiniteQuery<GetPropertiesResponseDto>({
    queryKey: [
      ...PROPERTIES_FOR_ENQUIRY_QUERY_KEY,
      propertyId,
      propertyTitle,
      privateInspectionAllowed,
      hasOpenInspections,
      5,
    ],
    queryFn: async ({ pageParam }) => {
      const response = await enquiryApi.enquiryControllerGetPropertiesForEnquiryV1({
        pageNo: pageParam as number,
        pageSize,
        ...(propertyId != null && propertyId !== "" && { propertyId }),
        ...(propertyTitle != null && propertyTitle !== "" && { propertyTitle }),
        ...(privateInspectionAllowed === "yes" && { privateInspectionAllowed: "yes" }),
        ...(hasOpenInspections === "yes" && { hasOpenInspections: "yes" }),
      });

      return response;
    },
    getNextPageParam: (lastPage) => {
      const pagination = lastPage.data?.pagination;
      if (!pagination) return undefined;
      const { pageNo, totalPages } = pagination;
      return pageNo < totalPages ? pageNo + 1 : undefined;
    },
    initialPageParam: 1,
    refetchOnMount: "always",
  });
};

export const useEnquiryDetails = (id: string | undefined) => {
  return useQuery<GetEnquiryDetailsApiResponseDto>({
    queryKey: [...ENQUIRY_DETAILS_QUERY_KEY, id],
    queryFn: async () => {
      const response = await enquiryApi.enquiryControllerGetEnquiryDetailsV1({
        id: id!,
      });
      return response;
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  });
};

export interface UseEnquiryCommentsParams {
  enquiryId: string | undefined;
  pageSize?: number;
}

export const useEnquiryComments = (params: UseEnquiryCommentsParams) => {
  const { enquiryId, pageSize = PAGINATION.DEFAULT_LIMIT } = params;

  return useInfiniteQuery<GetEnquiryCommentsResponseDto>({
    queryKey: [...ENQUIRY_COMMENTS_QUERY_KEY, enquiryId, pageSize],
    queryFn: async ({ pageParam }) => {
      const response = await enquiryApi.enquiryControllerGetCommentsV1({
        enquiryId: enquiryId!,
        pageNo: pageParam as number,
        pageSize,
      });
      return response;
    },
    getNextPageParam: (lastPage) => {
      const pagination = lastPage.data?.pagination;
      if (!pagination) return undefined;
      const { pageNo, totalPages } = pagination;
      return pageNo < totalPages ? pageNo + 1 : undefined;
    },
    initialPageParam: 1,
    enabled: !!enquiryId,
    staleTime: 1000 * 60 * 5,
  });
};

/** Input for adding a comment to an enquiry. File: jpg, jpeg, png, pdf only; max 10MB. Optional. */
export interface AddEnquiryCommentInput {
  /** Enquiry ID */
  enquiryId: string;
  /** Comment message */
  message: string;
  /** Optional supporting document. Only jpg, jpeg, png, pdf allowed. Max 10MB. */
  file?: Blob;
}

/**
 * Hook to add a comment to an enquiry via POST /v1/enquiries/{id}/comment.
 * Takes enquiryId and message (comment); optional file (jpg, jpeg, png, pdf; max 10MB).
 * Invalidates enquiry comments and enquiry details for the given enquiry on success.
 */
export function useAddEnquiryComment() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation<AddEnquiryCommentResponseDto, Error, AddEnquiryCommentInput>({
    mutationFn: async (input) => {
      return await enquiryApi.enquiryControllerAddCommentV1({
        id: input.enquiryId,
        comment: input.message,
        ...(input.file != null && { file: input.file }),
      });
    },
    onSuccess: (data, variables) => {
      toast.success(data.message ?? "Comment added successfully");
      queryClient.invalidateQueries({
        queryKey: [...ENQUIRY_COMMENTS_QUERY_KEY, variables.enquiryId],
      });
      queryClient.invalidateQueries({
        queryKey: [...ENQUIRY_DETAILS_QUERY_KEY, variables.enquiryId],
      });
    },
    mutationKey: [...ADD_ENQUIRY_COMMENT_MUTATION_KEY],
    onError: (error) => {
      handleHttpError(error, "Failed to add comment", toast);
    },
  });
}

export interface CreateDocumentRequestEnquiryInput {
  /** Listing ID (required) */
  listingId: string;
  /** Property media IDs to request (required) */
  propertyMediaIds: string[];
  /** Email for the document request */
  email: string;
  /** Whether B&P (Building and Pest) inspection is required. Defaults to false */
  isBAndPInspectionRequired?: boolean;
}

/**
 * Hook to create a document-request enquiry via POST /v1/enquiries/document-request.
 * Takes listingId and propertyMediaIds as required; pass email and optional isBAndPInspectionRequired when calling mutate().
 */
export function useCreateDocumentRequestEnquiry() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation<
    DocumentRequestEnquiryApiResponseDto,
    Error,
    CreateDocumentRequestEnquiryInput
  >({
    mutationFn: async (input) => {
      return await enquiryApi.enquiryControllerCreateDocumentRequestEnquiryV1({
        documentRequestEnquiryInputDto: {
          email: input.email,
          listingId: input.listingId,
          propertyMediaIds: input.propertyMediaIds,
          isBAndPInspectionRequired: input.isBAndPInspectionRequired ?? false,
        },
      });
    },
    onSuccess: (data) => {
      toast.success(data.message ?? "Document request enquiry created successfully");
      queryClient.invalidateQueries({ queryKey: MY_ENQUIRY_QUERY_KEY });
    },
    mutationKey: [...CREATE_DOCUMENT_REQUEST_MUTATION_KEY],
    onError: (error) => {
      handleHttpError(error, "Failed to create document request enquiry", toast);
    },
  });
}

export interface CreateGeneralEnquiryInput {
  name: string;
  email: string;
  listingId?: string;
  query: GeneralEnquiryInputDtoQueryEnum;
  details: string;
}

export function useCreateGeneralEnquiry() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation<GeneralEnquiryApiResponseDto, Error, CreateGeneralEnquiryInput>({
    mutationFn: async (input) => {
      return await enquiryApi.enquiryControllerCreateGeneralEnquiryV1({
        generalEnquiryInputDto: {
          email: input.email,
          ...(input.listingId ? { listingId: input.listingId } : {}),
          name: input.name,
          query: input.query,
          details: input.details,
        },
      });
    },
    onSuccess: (data) => {
      toast.success(data.message ?? "General enquiry created successfully");
      queryClient.invalidateQueries({ queryKey: MY_ENQUIRY_QUERY_KEY });
    },
    mutationKey: [...CREATE_GENERAL_ENQUIRY_MUTATION_KEY],
    onError: (error) => {
      handleHttpError(error, "Failed to create general enquiry", toast);
    },
  });
}

/** Open inspection: requires listingInspectionId; optional start/end times from selected slot */
export interface CreateOpenInspectionInput {
  inspectionType: "open";
  email: string;
  name?: string;
  countryCode?: string;
  phoneNumber?: string;
  listingId: string;
  listingInspectionId: string;
  inspectionStartTime?: string;
  inspectionEndTime?: string;
}

/** Private inspection: requires inspectionStartTime and inspectionEndTime */
export interface CreatePrivateInspectionInput {
  inspectionType: "private";
  email: string;
  name?: string;
  countryCode?: string;
  phoneNumber?: string;
  listingId: string;
  inspectionStartTime: string;
  inspectionEndTime: string;
}

/** Discriminated union: send listingInspectionId for open, inspectionStartTime/inspectionEndTime for private */
export type CreateInspectionInput = CreateOpenInspectionInput | CreatePrivateInspectionInput;

/**
 * Hook to create a book-inspection enquiry via POST /v1/inspections.
 * Two inspection types:
 * - **open**: requires listingInspectionId (existing open slot).
 * - **private**: requires inspectionStartTime and inspectionEndTime.
 * Common fields: email, listingId, inspectionType.
 */
export function useCreateInspection() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation<BookInspectionEnquiryApiResponseDto, Error, CreateInspectionInput>({
    mutationFn: async (input) => {
      const base = {
        email: input.email,
        ...(input.name != null && { name: input.name }),
        ...(input.countryCode != null && { countryCode: input.countryCode }),
        ...(input.phoneNumber != null && { phoneNumber: input.phoneNumber }),
        listingId: input.listingId,
        inspectionType: input.inspectionType,
      };
      const body =
        input.inspectionType === "open"
          ? {
              ...base,
              listingInspectionId: input.listingInspectionId,
              ...(input.inspectionStartTime != null && {
                inspectionStartTime: input.inspectionStartTime,
              }),
              ...(input.inspectionEndTime != null && {
                inspectionEndTime: input.inspectionEndTime,
              }),
            }
          : {
              ...base,
              inspectionStartTime: input.inspectionStartTime,
              inspectionEndTime: input.inspectionEndTime,
            };
      return await inspectionApi.inspectionControllerBookInspectionV1({
        bookInspectionDto: body,
      });
    },
    onSuccess: (data) => {
      toast.success(data.message ?? "Inspection enquiry created successfully");
      queryClient.invalidateQueries({ queryKey: MY_ENQUIRY_QUERY_KEY });
    },
    mutationKey: [...CREATE_INSPECTION_MUTATION_KEY],
    onError: (error) => {
      handleHttpError(error, "Failed to create inspection enquiry", toast);
    },
  });
}

/** Request body for property valuation enquiry */
export interface CreatePropertyValuationEnquiryInput {
  avmPropertyId: string;
  lastSoldDate?: string | Date | null;
  lastSoldPrice?: number | null;
  renovated: boolean;
  description?: string | null;
  isIntrestedInListing: boolean;
  name: string;
  email: string;
  countryCode?: string | null;
  phoneNumber?: string | null;
  files?: Blob[];
}

/**
 * Hook to create a property valuation enquiry via POST /v1/enquiries/property-valuation.
 * Uses multipart form data. Response includes enquiryId in data.
 */
export function useCreatePropertyValuationEnquiry() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation<
    PropertyValuationEnquiryApiResponseDto,
    Error,
    CreatePropertyValuationEnquiryInput
  >({
    mutationFn: async () => {
      // const requestParams: EnquiryControllerCreatePropertyValuationEnquiryV1Request = {
      //   avmPropertyId: input.avmPropertyId,
      //   renovated: input.renovated,
      //   isInterestedInListing: input.isIntrestedInListing,
      //   name: input.name,
      //   email: input.email,
      //   files: input.files ?? [],
      //   ...(input.lastSoldDate != null && {
      //     lastSoldDate:
      //       typeof input.lastSoldDate === "string"
      //         ? new Date(input.lastSoldDate)
      //         : input.lastSoldDate,
      //   }),
      //   ...(input.lastSoldPrice != null && {
      //     lastSoldPrice: input.lastSoldPrice,
      //   }),
      //   ...(input.description != null &&
      //     input.description !== "" && { description: input.description }),
      //   ...(input.countryCode != null &&
      //     input.countryCode !== "" && { countryCode: input.countryCode }),
      //   ...(input.phoneNumber != null &&
      //     input.phoneNumber !== "" && { phoneNumber: input.phoneNumber }),
      // };
      // const apiResponse =
      //   await enquiryApi.enquiryControllerCreatePropertyValuationEnquiryV1Raw(requestParams);
      // const json = (await apiResponse.raw.json()) as PropertyValuationEnquiryApiResponseDto;
      // return json;
      const mockResponse: PropertyValuationEnquiryApiResponseDto = {
        statusCode: 200,
        status: "success",
        message: "Property valuation enquiry created (mock response).",
        data: { enquiryId: "mock-enquiry-id" },
        error: null,
      };

      return mockResponse;
    },
    onSuccess: (data) => {
      toast.success(data.message ?? "Property valuation enquiry created successfully");
      queryClient.invalidateQueries({ queryKey: MY_ENQUIRY_QUERY_KEY });
    },
    mutationKey: [...CREATE_PROPERTY_VALUATION_ENQUIRY_MUTATION_KEY],
    onError: (error) => {
      handleHttpError(error, "Failed to create property valuation enquiry", toast);
    },
  });
}

export function useSendEnquiryDocuments(enquiryId: string | undefined) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation<SendEnquiryDocumentsResponseDto, Error, void>({
    mutationFn: async () => {
      if (!enquiryId) throw new Error("Enquiry ID is required to send documents.");
      return enquiryApi.enquiryControllerSendEnquiryDocumentsV1({ id: enquiryId });
    },
    onSuccess: (data) => {
      toast.success(data.message ?? "Documents sent successfully.");
      void queryClient.invalidateQueries({ queryKey: [...ENQUIRY_DETAILS_QUERY_KEY, enquiryId] });
    },
    onError: (error) => {
      handleHttpError(error, "Failed to send documents.", toast);
    },
  });
}

export function useUpdateEnquiryStatus(enquiryId: string | undefined) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation<UpdateEnquiryStatusResponseDto, Error, UpdateEnquiryStatusBodyDtoStatusEnum>({
    mutationFn: async (status) => {
      if (!enquiryId) throw new Error("Enquiry ID is required to update status.");
      return enquiryApi.enquiryControllerUpdateEnquiryStatusV1({
        id: enquiryId,
        updateEnquiryStatusBodyDto: { status },
      });
    },
    onSuccess: (data) => {
      toast.success(data.message ?? "Enquiry status updated successfully.");
      void queryClient.invalidateQueries({ queryKey: [...ENQUIRY_DETAILS_QUERY_KEY, enquiryId] });
      void queryClient.invalidateQueries({ queryKey: MY_ENQUIRY_QUERY_KEY });
    },
    onError: (error) => {
      handleHttpError(error, "Failed to update enquiry status.", toast);
    },
  });
}

export function useUpdateInspectionApproval(
  enquiryId: string | undefined,
  inspectionId: string | undefined,
) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation<UpdateInspectionApprovalResponseDto, Error, boolean>({
    mutationFn: async (isApproved) => {
      if (!enquiryId) throw new Error("Enquiry ID is required.");
      if (!inspectionId) throw new Error("Inspection ID is required.");
      return enquiryApi.enquiryControllerUpdateInspectionApprovalV1({
        id: inspectionId,
        updateInspectionApprovalBodyDto: { isApproved },
      });
    },
    onSuccess: (data) => {
      toast.success(
        (data as { message?: string }).message ?? "Inspection booking updated successfully.",
      );
      void queryClient.invalidateQueries({ queryKey: [...ENQUIRY_DETAILS_QUERY_KEY, enquiryId] });
      void queryClient.invalidateQueries({ queryKey: MY_ENQUIRY_QUERY_KEY });
    },
    onError: (error) => {
      handleHttpError(error, "Failed to update inspection booking.", toast);
    },
  });
}
